import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { createPost, updatePost, getPostAdmin, getPostById } from '../../services/blogApi';
import { toast } from 'react-toastify';

export default function BlogEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    title: '', excerpt: '', content: '', category: '', published: false,
  });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      getPostAdmin(id)
        .then(res => {
          const p = res.data;
          setForm({
            title: p.title || '',
            excerpt: p.excerpt || '',
            content: p.content || '',
            category: p.category || '',
            published: Boolean(p.published),
          });
          if (p.coverImageUrl) setCoverPreview(p.coverImageUrl);
        })
        .catch(() => toast.error('Failed to load post'))
        .finally(() => setFetching(false));
    }
  }, [id, isEdit]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) { toast.error('Title and content are required'); return; }

    setLoading(true);
    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => formData.append(k, v));
    if (coverFile) formData.append('coverImage', coverFile);

    try {
      if (isEdit) {
        await updatePost(id, formData);
        toast.success('Post updated!');
      } else {
        await createPost(formData);
        toast.success('Post created!');
      }
      navigate('/admin');
    } catch {
      toast.error('Failed to save post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7fafc' }}>
      <i className="fas fa-spinner fa-spin" style={{ color: '#7C4DFF', fontSize: '3rem' }} />
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f7fafc' }}>
      {/* Topbar */}
      <div style={{
        background: '#0A2540', padding: '0 24px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1.2rem', color: '#fff' }}>
          Dennis<span style={{ color: '#7C4DFF' }}>.</span>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', fontWeight: 400, marginLeft: 12 }}>
            {isEdit ? 'Edit Post' : 'New Post'}
          </span>
        </div>
        <Link to="/admin" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>← Back to Dashboard</Link>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24, alignItems: 'start' }}>
            {/* Main content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Title */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                <label style={labelStyle}>Post Title *</label>
                <input
                  name="title" value={form.title} onChange={handleChange}
                  placeholder="Enter post title..."
                  style={{ ...inputStyle, fontSize: '1.2rem', fontWeight: 600 }}
                />
              </div>

              {/* Excerpt */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                <label style={labelStyle}>Excerpt / Summary</label>
                <textarea name="excerpt" value={form.excerpt} onChange={handleChange} rows={2}
                  placeholder="A brief description shown in post listings..."
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>

              {/* Content */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                <label style={labelStyle}>Content * (HTML supported)</label>
                <textarea name="content" value={form.content} onChange={handleChange} rows={20}
                  placeholder="Write your blog post content here. HTML tags are supported for formatting..."
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.9rem' }}
                />
                <p style={{ color: '#718096', fontSize: '0.78rem', marginTop: 8 }}>
                  <i className="fas fa-info-circle" style={{ marginRight: 4 }} />
                  HTML is supported. Use &lt;h2&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;img&gt;, &lt;blockquote&gt;, etc.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Publish settings */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                <h3 style={{ color: '#0A2540', fontSize: '0.95rem', fontWeight: 700, marginBottom: 16 }}>Publish</h3>

                {/* Status badge */}
                <div style={{
                  background: form.published ? 'rgba(124,77,255,0.08)' : 'rgba(237,137,54,0.08)',
                  border: `1px solid ${form.published ? 'rgba(124,77,255,0.3)' : 'rgba(237,137,54,0.3)'}`,
                  borderRadius: 8, padding: '8px 12px', marginBottom: 14,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: form.published ? '#7C4DFF' : '#dd6b20',
                  }} />
                  <span style={{ color: form.published ? '#7C4DFF' : '#dd6b20', fontSize: '0.82rem', fontWeight: 700 }}>
                    {form.published ? 'Currently Published' : 'Currently Draft'}
                  </span>
                </div>

                {/* Toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 20 }}
                  onClick={() => setForm(f => ({ ...f, published: !f.published }))}>
                  <div style={{
                    width: 46, height: 26, borderRadius: 13,
                    background: form.published ? '#7C4DFF' : '#cbd5e0',
                    position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                  }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: 3,
                      left: form.published ? 23 : 3,
                      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </div>
                  <input type="checkbox" name="published" checked={form.published} onChange={handleChange} style={{ display: 'none' }} />
                  <span style={{ color: '#2D3748', fontSize: '0.9rem', fontWeight: 600 }}>
                    {form.published ? 'Published — visible to public' : 'Draft — hidden from public'}
                  </span>
                </label>

                <button type="submit" disabled={loading} style={{
                  width: '100%', padding: '12px', background: '#7C4DFF', color: '#fff',
                  border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.95rem',
                  cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                  {loading ? <><i className="fas fa-spinner fa-spin" /> Saving...</> : <><i className="fas fa-save" /> {isEdit ? 'Update Post' : 'Create Post'}</>}
                </button>
              </div>

              {/* Category */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                <label style={labelStyle}>Category</label>
                <select name="category" value={form.category} onChange={handleChange}
                  style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">Select category...</option>
                  {['Digital Finance', 'Financial Inclusion', 'Development', 'Research', 'Capacity Building', 'Product Development', 'Africa Insights', 'Other'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Cover image */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                <label style={labelStyle}>Cover Image</label>
                {coverPreview ? (
                  <div style={{ position: 'relative', marginBottom: 10 }}>
                    <img src={coverPreview} alt="cover" style={{ width: '100%', borderRadius: 8, objectFit: 'cover', height: 140 }} />
                    <button type="button" onClick={() => { setCoverFile(null); setCoverPreview(null); if (fileRef.current) fileRef.current.value = ''; }}
                      style={{
                        position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)',
                        color: '#fff', border: 'none', borderRadius: '50%', width: 24, height: 24,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      ×
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileRef.current?.click()}
                    style={{
                      border: '2px dashed #e2e8f0', borderRadius: 8, padding: '24px 16px',
                      textAlign: 'center', cursor: 'pointer', marginBottom: 10, transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#7C4DFF'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                  >
                    <i className="fas fa-image" style={{ color: '#7C4DFF', fontSize: '1.5rem', marginBottom: 8 }} />
                    <p style={{ color: '#718096', fontSize: '0.82rem' }}>Click to upload cover image</p>
                    <p style={{ color: '#cbd5e0', fontSize: '0.75rem' }}>JPG, PNG, WebP supported</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', color: '#4a5568', fontSize: '0.82rem', fontWeight: 700,
  marginBottom: 8, letterSpacing: '0.3px',
};

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 8,
  border: '1px solid #e2e8f0', background: '#f7fafc',
  color: '#0A2540', fontSize: '0.92rem', fontFamily: 'inherit', outline: 'none',
  transition: 'border-color 0.2s',
};
